import { useEffect, useState } from 'react';
import { Check, ChevronDown, Crop, X } from 'lucide-react';

// Visual walkthrough of how to crop the SOLS screenshot: a mock of the
// grades table with only the grades (below the GRADE heading) circled, plus a good / bad pair.
// Open by default; the collapsed state is remembered per device.

const GUIDE_KEY = 'uspgpa.guideCollapsed.v1';

// Made-up rows, styled like the SOLS table (alternating blue / white).
const SAMPLE_ROWS = [
  ['202301', 'MA111', 'CALCULUS I & LINEAR ALG', 'A+'],
  ['202301', 'ST131', 'INTRO TO STATISTICS', 'A'],
  ['202303', 'CS001', 'FNDTNS OF PROF PRACT', 'IP'],
  ['202303', 'CS112', 'DATA STRUCTURES & ALGOS', 'C+'],
  ['202303', 'CS140', 'INTRO TO SOFTWARE ENG', 'B+'],
  ['202401', 'CS211', 'COMPUTER ORGANISATION', 'B+'],
  ['202401', 'IS221', 'WEB APPLICATIONS DEV', 'A'],
];

const rowTint = (i) => (i % 2 === 0 ? 'bg-[#cfe2f3]' : 'bg-white');

const isCollapsed = () => {
  try {
    return window.localStorage.getItem(GUIDE_KEY) === '1';
  } catch {
    return false;
  }
};

// The annotated mock table: everything except the grades is faded out, and
// the grades (below the GRADE heading) carry a red outline. The header row
// has a fixed height so the outline can start just beneath it.
const TableMock = () => (
  <div className="relative rounded-xl bg-white overflow-hidden text-slate-800 shadow-inner">
    <table className="w-full text-[9.5px] leading-none table-fixed" aria-hidden="true">
      <colgroup>
        <col className="w-[17%]" />
        <col className="w-[16%]" />
        <col />
        <col className="w-[21%]" />
      </colgroup>
      <thead>
        <tr className="bg-slate-100 font-semibold h-6">
          <th className="text-left px-1.5 py-1.5 opacity-35">TERM</th>
          <th className="text-left px-1.5 py-1.5 opacity-35">COURSE</th>
          <th className="text-left px-1.5 py-1.5 opacity-35">TITLE</th>
          <th className="text-center px-1.5 py-1.5 opacity-35">GRADE</th>
        </tr>
      </thead>
      <tbody>
        {SAMPLE_ROWS.map((row, i) => (
          <tr key={row[1] + i} className={rowTint(i)}>
            <td className="px-1.5 py-[5px] opacity-35">{row[0]}</td>
            <td className="px-1.5 py-[5px] opacity-35">{row[1]}</td>
            <td className="px-1.5 py-[5px] opacity-35 truncate">{row[2]}</td>
            <td className="px-1.5 py-[5px] text-center font-semibold">{row[3]}</td>
          </tr>
        ))}
      </tbody>
    </table>

    {/* red outline around the grades only, not the GRADE heading */}
    <div
      className="pointer-events-none absolute top-[25px] bottom-0.5 right-0.5 w-[21%] rounded-[14px] border-[2.5px] border-red-500"
      style={{ boxShadow: '0 0 0 3px rgba(239,68,68,0.18)' }}
    />
  </div>
);

// Tiny thumbnails for the "do this / not this" comparison.
const MiniColumn = () => (
  <div className="mx-auto w-9 rounded-md overflow-hidden border border-slate-300 bg-white">
    {[0, 1, 2, 3, 4, 5].map(i => (
      <div key={i} className={`h-2 ${rowTint(i)} flex items-center justify-center`}>
        <span className="block w-2 h-[3px] rounded-sm bg-slate-600" />
      </div>
    ))}
  </div>
);

const MiniTable = () => (
  <div className="mx-auto w-full max-w-[110px] rounded-md overflow-hidden border border-slate-300 bg-white">
    <div className="h-2 bg-slate-100" />
    {[0, 1, 2, 3, 4, 5].map(i => (
      <div key={i} className={`h-2 ${rowTint(i)} flex items-center gap-1 px-1`}>
        <span className="block w-3 h-[3px] rounded-sm bg-slate-500" />
        <span className="block w-3 h-[3px] rounded-sm bg-slate-500" />
        <span className="block flex-1 h-[3px] rounded-sm bg-slate-400" />
        <span className="block w-2 h-[3px] rounded-sm bg-slate-600" />
      </div>
    ))}
  </div>
);

const STEPS = [
  {
    title: 'Open your grades on SOLS',
    body: 'Go to the page that lists every course you have taken with its grade.',
  },
  {
    title: 'Screenshot only the grades',
    body: 'Start at the first grade just below the GRADE heading and drag down to the last grade. Leave out the heading itself, and the term, course, title, campus and mode columns.',
  },
  {
    title: 'Upload it below',
    body: 'Zoom the page out first if all your grades don’t fit in one screenshot.',
  },
];

const SHORTCUTS = [
  { device: 'Windows', how: 'Win + Shift + S, then drag a box' },
  { device: 'Mac', how: '⌘ + Shift + 4, then drag a box' },
  { device: 'Phone', how: 'Take a screenshot, then Edit → Crop' },
];

// `forceOpen` re-expands the guide (e.g. after a scan that found nothing).
const ScreenshotGuide = ({ forceOpen = false }) => {
  const [open, setOpen] = useState(() => !isCollapsed());

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      window.localStorage.setItem(GUIDE_KEY, next ? '0' : '1');
    } catch {
      // private browsing, just skip persistence
    }
  };

  return (
    <div className="rounded-3xl bg-white/[0.06] border border-white/10">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left"
      >
        <span className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-xl bg-red-500/15 flex items-center justify-center">
            <Crop className="w-4 h-4 text-red-300" />
          </span>
          <span>
            <span className="block text-[15px] font-semibold">How to take the screenshot</span>
            <span className="block text-xs text-white/55">
              Capture the grades only, not the heading
            </span>
          </span>
        </span>
        <ChevronDown
          className={`w-5 h-5 text-white/60 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="px-4 pb-5">
          <div className="relative pb-5">
            <TableMock />
            <span className="absolute bottom-0 right-0 rounded-full bg-red-500 text-white text-[11px] font-semibold px-2.5 py-1 shadow-lg shadow-red-900/30">
              Only this part
            </span>
          </div>

          <ol className="mt-4 space-y-3.5">
            {STEPS.map((step, i) => (
              <li key={step.title} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-white text-slate-900 text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <div>
                  <p className="text-sm font-semibold leading-6">{step.title}</p>
                  <p className="text-[13px] text-white/60 leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-5 grid grid-cols-2 gap-2.5">
            <div className="rounded-2xl bg-mint-300/10 border border-mint-300/30 p-3 text-center">
              <MiniColumn />
              <p className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-mint-200">
                <Check className="w-3.5 h-3.5" /> Grades only
              </p>
            </div>
            <div className="rounded-2xl bg-red-500/10 border border-red-400/30 p-3 text-center">
              <MiniTable />
              <p className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-red-300">
                <X className="w-3.5 h-3.5" /> Not the whole table
              </p>
            </div>
          </div>

          <dl className="mt-5 rounded-2xl bg-black/15 divide-y divide-white/10 text-[13px]">
            {SHORTCUTS.map(s => (
              <div key={s.device} className="flex items-baseline gap-3 px-3.5 py-2.5">
                <dt className="w-16 shrink-0 font-semibold text-white/85">{s.device}</dt>
                <dd className="text-white/60">{s.how}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
};

export default ScreenshotGuide;
